import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  users, decks, build, type DeckConcept, type DasResult, type DasSeedReport, type SeedOutcome, type CardEntry, type CardMeta, type TagGroup,
} from '@/lib/api';
import { CardAutocomplete } from '@/components/CardAutocomplete';
import { AnalysisText } from '@/components/AnalysisText';
import { DecklistWidget } from '@/components/DecklistWidget';
import { FitReadout } from '@/components/GoalsFit';
import { showCardTooltip, hideCardTooltip } from '@/components/CardTooltip';
import { cardImageUris } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { ROLE_LABELS } from '@/lib/buildRoles';
import { isEmptyGoals, normalizeGoals, parseGoalNumber } from '@/lib/goals';
import { DAS_COPY, EVALUATE_COPY, NEW_CARD_LAND_REASON_COPY } from '@/lib/rankingTerms';

const DAS_SESSION_KEY = 'das_active_run';

/** A seed's outcome badge. Included is new copy; every other state is the evaluate-cards verdict wording. */
const SEED_LABEL: Record<SeedOutcome, string> = {
  pip_uncastable: DAS_COPY.seedPipUncastable,
  included: DAS_COPY.seedIncluded,
  land_included: EVALUATE_COPY.verdictLabel.land_admitted,
  land_held: EVALUATE_COPY.verdictLabel.land_held,
  not_found: EVALUATE_COPY.verdictLabel.not_found,
  duplicate: EVALUATE_COPY.verdictLabel.already_in_deck,
  color_identity: EVALUATE_COPY.verdictLabel.color_identity,
  not_legal: EVALUATE_COPY.verdictLabel.not_legal,
  no_role_fit: EVALUATE_COPY.verdictLabel.no_role_fit,
};

const SEED_PROBLEMS: ReadonlySet<SeedOutcome> = new Set<SeedOutcome>([
  'not_found', 'duplicate', 'color_identity', 'not_legal', 'no_role_fit', 'land_held', 'pip_uncastable',
]);

/** The explanatory line under a seed, or null when the badge says it all. */
function seedLine(s: DasSeedReport): string | null {
  switch (s.outcome) {
    case 'included': return null;
    case 'land_included': return EVALUATE_COPY.landAdmitted;
    case 'land_held': return s.reason && s.reason in NEW_CARD_LAND_REASON_COPY
      ? NEW_CARD_LAND_REASON_COPY[s.reason as keyof typeof NEW_CARD_LAND_REASON_COPY] : null;
    case 'not_found': return EVALUATE_COPY.notFound(s.requested);
    case 'duplicate': return EVALUATE_COPY.alreadyInDeck;
    case 'color_identity': return EVALUATE_COPY.colorIdentity;
    case 'not_legal': return EVALUATE_COPY.notLegal;
    case 'no_role_fit': return EVALUATE_COPY.noRoleFit;
    case 'pip_uncastable': return DAS_COPY.seedPipUncastableLine(s.reason ?? '');
    default: return null;
  }
}

const roleLabel = (role: string) => ROLE_LABELS[role] ?? role;

/** Where a save stopped. `deckId` is set once the deck exists, so the user can still open it. */
interface SaveProblem { message: string; deckId: string | null }

// ── Bracket labels ────────────────────────────────────────────────────────────
const BRACKETS = [
  { value: 1, label: 'B1 — Casual' },
  { value: 2, label: 'B2 — Upgraded' },
  { value: 3, label: 'B3 — Optimized' },
  { value: 4, label: 'B4 — High Power' },
  { value: 5, label: 'B5 — cEDH' },
];

// ── Types ─────────────────────────────────────────────────────────────────────
type Step = 'input' | 'concepts' | 'building' | 'result';

// ── Main component ────────────────────────────────────────────────────────────

interface DecASketchPanelProps {
  /** If provided, immediately enter the building state polling this run. */
  initialRunId?: number | null;
}

export function DecASketchPanel({ initialRunId }: DecASketchPanelProps = {}) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('input');
  const [commander, setCommander] = useState('');
  const [theme, setTheme] = useState('');
  const [bracket, setBracket] = useState(3);
  // Raw field text: blank = no limit (null on the wire, never 0, which would mean $0).
  const [deckBudget, setDeckBudget] = useState('');
  const [perCardCap, setPerCardCap] = useState('');
  const [concepts, setConcepts] = useState<DeckConcept[]>([]);
  const [chosen, setChosen] = useState<DeckConcept | null>(null);
  const [result, setResult] = useState<DasResult | null>(null);
  // A completed run stored before the deterministic rework has no structured result.
  const [olderFormat, setOlderFormat] = useState(false);
  const [deckName, setDeckName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveProblem, setSaveProblem] = useState<SaveProblem | null>(null);
  const [runId, setRunId] = useState<number | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);

  // Resume from prop or sessionStorage on first render
  useEffect(() => {
    if (initialRunId != null) {
      setRunId(initialRunId);
      setStep('building');
      return;
    }
    const saved = sessionStorage.getItem(DAS_SESSION_KEY);
    if (saved) {
      try {
        const { runId: savedId, commander: savedCmd } = JSON.parse(saved) as { runId: number; commander?: string };
        if (savedId) {
          setRunId(savedId);
          if (savedCmd) setCommander(savedCmd);
          setStep('building');
        }
      } catch {
        sessionStorage.removeItem(DAS_SESSION_KEY);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for build completion while in the 'building' step
  useEffect(() => {
    if (step !== 'building' || runId == null) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const run = await users.decaSketchGetRun(runId);
        if (cancelled) return;
        if (run.status === 'complete') {
          sessionStorage.removeItem(DAS_SESSION_KEY);
          setResult(run.result ?? null);
          setOlderFormat(!run.result);
          setDeckName(`${run.commander} (Sketch)`);
          setStep('result');
        } else if (run.status === 'error') {
          sessionStorage.removeItem(DAS_SESSION_KEY);
          setPollError(run.notes ?? 'Build failed');
        }
      } catch {
        // Transient fetch error — keep polling
      }
    };
    void poll();
    const interval = setInterval(() => { void poll(); }, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [step, runId]);

  // Step 1 → concepts
  const conceptsMutation = useMutation({
    mutationFn: () => users.decaSketchConcepts(commander.trim(), theme.trim(), bracket),
    onSuccess: (data) => {
      setConcepts(data.concepts);
      setStep('concepts');
    },
  });

  // Step 2 → start build (returns run_id immediately; polling handles completion)
  const buildMutation = useMutation({
    mutationFn: (concept: DeckConcept) =>
      users.decaSketchBuild(concept.commander, concept.strategy, bracket, {
        deck_budget_usd: parseGoalNumber(deckBudget),
        budget_per_card_usd: parseGoalNumber(perCardCap),
      }, concept.seed_cards ?? [], concept.fill_themes ?? []),
    onSuccess: (data) => {
      setRunId(data.run_id);
      sessionStorage.setItem(DAS_SESSION_KEY, JSON.stringify({
        runId: data.run_id,
        commander: chosen?.commander ?? commander,
      }));
      setStep('building');
    },
  });

  const handlePickConcept = useCallback((concept: DeckConcept) => {
    setChosen(concept);
    buildMutation.mutate(concept);
  }, [buildMutation]);

  const handleReset = () => {
    sessionStorage.removeItem(DAS_SESSION_KEY);
    setStep('input');
    setCommander('');
    setTheme('');
    setDeckBudget('');
    setPerCardCap('');
    setConcepts([]);
    setChosen(null);
    setResult(null);
    setOlderFormat(false);
    setSaveProblem(null);
    setRunId(null);
    setPollError(null);
    conceptsMutation.reset();
    buildMutation.reset();
  };

  const handleReject = () => {
    if (runId != null) {
      void users.updateDasRunOutcome(runId, 'rejected').catch(() => undefined);
    }
    handleReset();
  };

  // The sketch's decklist (name -> qty) in CardEntry form for DecklistWidget, with the commander
  // included so it gets highlighted.
  const cardEntries = useMemo<Record<string, CardEntry>>(() => {
    if (!result) return {};
    const entries: Record<string, CardEntry> = { [result.commander]: { qty: 1 } };
    for (const { name, qty } of result.decklist) entries[name] = { qty };
    return entries;
  }, [result]);

  const cardNames = useMemo(() => Object.keys(cardEntries), [cardEntries]);

  const { data: cardMetaData } = useQuery<Record<string, CardMeta>>({
    queryKey: ['sketchCardMeta', cardNames],
    queryFn: () => decks.getCardMetadataByNames(cardNames),
    enabled: cardNames.length > 0,
    staleTime: Infinity,
  });

  const { data: tagGroups } = useQuery<TagGroup[]>({
    queryKey: ['sketchTagGroups', cardNames],
    queryFn: () => decks.getTagGroupsByNames(cardNames),
    enabled: cardNames.length > 0,
    staleTime: Infinity,
  });

  const cardMeta = cardMetaData ?? {};
  const totalCards = result ? 1 + result.decklist.reduce((n, c) => n + c.qty, 0) : 0;
  const deckBudgetCap = result?.fit_report.goals?.deck_budget_usd ?? null;

  // Four sequential steps, each reported on its own: a failure after the deck exists must say so.
  const handleSave = async () => {
    if (!result || !deckName.trim()) return;
    setIsSaving(true);
    setSaveProblem(null);
    const message = (e: unknown) => (e instanceof Error ? e.message : String(e));
    let deckId: string;
    try {
      // The commander goes in as a regular "1 <name>" line so it is stored and highlighted,
      // mirroring how Moxfield imports work.
      const decklist = [`1 ${result.commander}`, ...result.decklist.map(c => `${c.qty} ${c.name}`)].join('\n');
      deckId = (await decks.addManual(deckName.trim(), 'commander', decklist, result.commander, 'dec-a-sketch')).id;
    } catch (e) {
      setSaveProblem({ message: DAS_COPY.deckFailed(message(e)), deckId: null });
      setIsSaving(false);
      return;
    }
    try {
      try {
        await build.setBracket(deckId, 'base', result.fit_report.bracket);
      } catch (e) {
        setSaveProblem({ message: DAS_COPY.bracketFailed(message(e)), deckId });
        return;
      }
      try {
        // The envelope the sketch was measured against; an empty one stores nothing.
        if (!isEmptyGoals(result.fit_report.goals)) {
          await build.setGoals(deckId, 'base', normalizeGoals(result.fit_report.goals));
        }
      } catch (e) {
        setSaveProblem({ message: DAS_COPY.goalsFailed(message(e)), deckId });
        return;
      }
      try {
        // Only a non-empty list: an empty write marks the themes chosen-empty and stops discovery seeding them.
        if ((result.themes ?? []).length > 0) {
          await build.setTemplate(deckId, 'base', { themes: result.themes, slot_overrides: {} });
        }
      } catch (e) {
        setSaveProblem({ message: DAS_COPY.themesFailed(message(e)), deckId });
        return;
      }
      try {
        if (runId != null) await users.updateDasRunOutcome(runId, 'saved', deckId);
      } catch (e) {
        setSaveProblem({ message: DAS_COPY.runFailed(message(e)), deckId });
        return;
      }
      toast.success(DAS_COPY.saved);
      navigate(`/deck/${deckId}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="text-sm">
      {/* ── Step: input ─────────────────────────────────────────────────── */}
      {step === 'input' && (
        <div>
          <p className="text-on-surface-variant mb-3 text-xs">{DAS_COPY.intro}</p>

          <div className="flex flex-col gap-3">
            {/* Commander (optional, autocomplete) */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-on-surface-variant">Commander <span className="opacity-50">(optional)</span></label>
              <CardAutocomplete
                value={commander}
                onChange={setCommander}
                placeholder="e.g. Meren of Clan Nel Toth"
                inputClassName="w-full bg-surface border border-outline-variant rounded-sm text-on-surface text-sm py-2 px-3 outline-none focus:border-primary"
              />
            </div>

            {/* Theme / notes (optional, plain text) */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-on-surface-variant">Theme / notes <span className="opacity-50">(optional)</span></label>
              <input
                type="text"
                value={theme}
                onChange={e => setTheme(e.target.value)}
                placeholder="e.g. zombie tribal, artifacts matter, aristocrats…"
                className="w-full bg-surface border border-outline-variant rounded-sm text-on-surface text-sm py-2 px-3 outline-none focus:border-primary"
              />
            </div>

            {/* Envelope: deck budget + per-card cap. Blank = no limit. */}
            <div className="flex flex-col gap-1">
              <div className="flex items-end gap-3 flex-wrap">
                {([
                  { label: DAS_COPY.deckBudgetLabel, value: deckBudget, set: setDeckBudget, step: 10 },
                  { label: DAS_COPY.perCardLabel, value: perCardCap, set: setPerCardCap, step: 1 },
                ] as const).map(f => (
                  <div key={f.label} className="flex flex-col gap-1">
                    <label className="text-xs text-on-surface-variant" htmlFor={`das-${f.label}`}>{f.label}</label>
                    <input
                      id={`das-${f.label}`}
                      type="number"
                      min={0}
                      step={f.step}
                      value={f.value}
                      onChange={e => f.set(e.target.value)}
                      placeholder={DAS_COPY.unlimited}
                      className="w-32 bg-surface border border-outline-variant rounded-sm text-on-surface text-sm py-2 px-3 outline-none focus:border-primary"
                    />
                  </div>
                ))}
                {(deckBudget !== '' || perCardCap !== '') && (
                  <button
                    onClick={() => { setDeckBudget(''); setPerCardCap(''); }}
                    className="text-xs text-on-surface-variant hover:text-primary underline pb-2"
                  >
                    {DAS_COPY.clear}
                  </button>
                )}
              </div>
              <span className="text-xs text-on-surface-variant opacity-70">{DAS_COPY.budgetHint}</span>
            </div>

            {/* Bracket chips */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-on-surface-variant mr-1">Bracket:</span>
              {BRACKETS.map(b => (
                <button
                  key={b.value}
                  onClick={() => setBracket(b.value)}
                  className={`h-7 px-2.5 rounded-full border text-xs font-medium cursor-pointer transition-colors ${
                    bracket === b.value
                      ? 'bg-primary text-on-primary border-primary'
                      : 'bg-transparent text-on-surface-variant border-outline-variant hover:border-primary hover:text-primary'
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => conceptsMutation.mutate()}
                disabled={conceptsMutation.isPending || (!commander.trim() && !theme.trim())}
                className="py-2 px-4 bg-primary text-on-primary border-none rounded-sm text-sm font-semibold cursor-pointer disabled:opacity-50"
              >
                {conceptsMutation.isPending ? 'Thinking…' : 'Suggest Concepts'}
              </button>
            </div>

            {conceptsMutation.isError && (
              <p className="text-error-text text-xs">
                {(conceptsMutation.error as Error)?.message ?? 'Something went wrong.'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Step: concepts ──────────────────────────────────────────────── */}
      {step === 'concepts' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-on-surface-variant">
              Choose a concept — we'll sketch the full deck from there.
            </span>
            <button onClick={handleReset} className="text-xs text-on-surface-variant hover:text-primary underline">
              {DAS_COPY.startOver}
            </button>
          </div>
          <div className="flex flex-col gap-2">
            {concepts.map((c, i) => (
              <div
                key={i}
                className="p-3 bg-surface rounded-sm border border-outline-variant/60 flex flex-col gap-1.5"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-on-surface">{c.title}</span>
                    <span
                      className="ml-2 text-xs text-on-surface-variant opacity-70 decklist-card cursor-help"
                      data-card-name={c.commander}
                      onMouseEnter={(e) => showCardTooltip(cardImageUris(c.commander), e)}
                      onMouseLeave={hideCardTooltip}
                    >{c.commander}</span>
                  </div>
                  <button
                    onClick={() => handlePickConcept(c)}
                    className="shrink-0 py-1 px-3 bg-primary text-on-primary border-none rounded-sm text-xs font-semibold cursor-pointer"
                  >
                    Pick This
                  </button>
                </div>
                <p className="text-xs text-on-surface-variant"><AnalysisText text={c.strategy} /></p>
                {(c.fill_themes ?? []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {(c.fill_themes ?? []).map(t => (
                      <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-container text-on-surface-variant">{t}</span>
                    ))}
                  </div>
                )}
                {(c.offcolor_refs ?? []).length > 0 && (
                  <div data-testid="offcolor-refs" className="text-xs text-warning-text flex flex-wrap items-center gap-x-1.5">
                    {(c.offcolor_refs ?? []).map(n => (
                      <span
                        key={n}
                        className="decklist-card cursor-help"
                        data-card-name={n}
                        onMouseEnter={(e) => showCardTooltip(cardImageUris(n), e)}
                        onMouseLeave={hideCardTooltip}
                      >{n}</span>
                    ))}
                    <span>{DAS_COPY.offcolorRefs(c.commander)}</span>
                  </div>
                )}
                {c.key_synergies?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {c.key_synergies.map((s, si) => (
                      <span
                        key={si}
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary"
                      >
                        <AnalysisText text={s} />
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Step: building ──────────────────────────────────────────────── */}
      {step === 'building' && (
        <div className="flex flex-col items-center gap-3 py-4">
          {chosen && (
            <div className="text-center mb-1">
              <div className="font-semibold text-on-surface">{chosen.title}</div>
              <div
                className="text-xs text-on-surface-variant decklist-card cursor-help"
                data-card-name={chosen.commander}
                onMouseEnter={(e) => showCardTooltip(cardImageUris(chosen.commander), e)}
                onMouseLeave={hideCardTooltip}
              >{chosen.commander}</div>
            </div>
          )}
          {pollError ? (
            <div className="flex flex-col items-center gap-2">
              <span className="material-symbols-outlined text-error-text text-xl leading-none">error</span>
              <p className="text-error-text text-sm text-center">{pollError}</p>
              <button onClick={handleReset} className="text-xs underline text-on-surface-variant">{DAS_COPY.startOver}</button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-on-surface-variant">
                <span className="material-symbols-outlined text-primary animate-spin text-xl leading-none">
                  progress_activity
                </span>
                <span className="text-sm">{DAS_COPY.building}</span>
              </div>
              <p className="text-xs text-on-surface-variant opacity-60 text-center max-w-xs">{DAS_COPY.buildingNote}</p>
              {buildMutation.isError && (
                <div className="mt-2 text-error-text text-xs text-center">
                  {(buildMutation.error as Error)?.message ?? 'Failed to start build.'}
                  <button onClick={() => setStep('concepts')} className="ml-2 underline">Back</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Step: result ────────────────────────────────────────────────── */}
      {step === 'result' && olderFormat && (
        <div className="flex flex-col gap-3">
          <div data-testid="older-format" className="p-3 bg-surface rounded-sm border border-outline-variant/50 text-xs text-on-surface-variant">
            {DAS_COPY.olderFormat}
          </div>
          <button onClick={handleReset} className="self-start text-xs text-on-surface-variant hover:text-primary underline">
            {DAS_COPY.startOver}
          </button>
        </div>
      )}

      {step === 'result' && result && (
        <div>
          {/* Header */}
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div>
              <span
                className="font-semibold text-on-surface decklist-card cursor-help"
                data-card-name={result.commander}
                onMouseEnter={(e) => showCardTooltip(cardImageUris(result.commander), e)}
                onMouseLeave={hideCardTooltip}
              >{result.commander}</span>
              <span className="ml-2 text-xs text-on-surface-variant">{DAS_COPY.cardCount(totalCards)}</span>
              <span data-testid="price-line" className="ml-2 text-xs font-medium text-on-surface-variant">
                · {DAS_COPY.priceLine(result.total_price_usd.toFixed(2), result.unpriced_count)}
                {deckBudgetCap != null && <span className="opacity-60"> {DAS_COPY.budgetOf(String(deckBudgetCap))}</span>}
              </span>
              {result.land_price_usd > 0 && (
                <div className="text-xs text-on-surface-variant opacity-70">
                  {DAS_COPY.landPriceLine(result.land_price_usd.toFixed(2))}
                </div>
              )}
            </div>
            <button onClick={handleReset} className="text-xs text-on-surface-variant hover:text-primary underline">
              {DAS_COPY.startOver}
            </button>
          </div>

          {/* Notes: absent when the narration failed; the deterministic result stands on its own */}
          {result.notes && (
            <div className="mb-3 p-3 bg-surface rounded-sm border border-outline-variant/50 text-xs text-on-surface leading-relaxed">
              <div className="font-semibold mb-1">{DAS_COPY.notesHeading}</div>
              <AnalysisText text={result.notes} />
            </div>
          )}

          {/* Themes used + thin-pool disclosure: absent when there is nothing to say */}
          {(result.themes ?? []).length > 0 && (
            <div data-testid="themes-used" className="mb-3 flex flex-wrap items-center gap-1 text-xs text-on-surface-variant">
              <span>{DAS_COPY.themesUsed}</span>
              {(result.themes ?? []).map(t => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-container">{t}</span>
              ))}
            </div>
          )}
          {result.edhrec_overlap && (
            <div data-testid="edhrec-overlap" className="mb-3 text-xs text-on-surface-variant">
              {DAS_COPY.overlap(result.edhrec_overlap.n, result.edhrec_overlap.of)}
            </div>
          )}
          {(result.theme_pool_notes ?? []).length > 0 && (
            <div data-testid="theme-pool-notes" className="mb-3 p-3 bg-surface-container rounded-sm text-xs">
              <p className="m-0 mb-1 text-on-surface-variant">{DAS_COPY.thinPool}</p>
              <ul className="m-0 p-0 list-none flex flex-col gap-0.5">
                {(result.theme_pool_notes ?? []).map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}

          {/* Curve discipline: absent on results stored before the report existed */}
          {result.curve_report && typeof result.curve_report.avg_mv === 'number' && (() => {
            const { avg_mv, horizon, p_development, p_solution, thin_pips } = result.curve_report;
            const pct = (p: number) => (p * 100).toFixed(0);
            return (
              <div data-testid="curve-report" className="mb-3 p-3 bg-surface-container rounded-sm text-xs">
                <div className="font-semibold mb-1">{DAS_COPY.curveHeading}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-on-surface-variant">
                  <span>{DAS_COPY.curveAvgMv(avg_mv.toFixed(2))}</span>
                  <span>{DAS_COPY.curveDevelopment(pct(p_development))}</span>
                  <span>{DAS_COPY.curveAnswer(pct(p_solution), horizon)}</span>
                </div>
                {thin_pips && thin_pips.length > 0 && (
                  <div data-testid="thin-pips" className="mt-1 text-on-surface-variant opacity-80">
                    {DAS_COPY.thinPips(thin_pips.join(', '))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Seed outcomes: every proposed seed is accounted for */}
          {result.seed_reports.length > 0 && (
            <div className="mb-3 p-3 bg-surface-container rounded-sm text-xs">
              <div className="font-semibold mb-1">{DAS_COPY.seedsHeading}</div>
              <ul className="m-0 p-0 list-none flex flex-col gap-1">
                {result.seed_reports.map((s, i) => {
                  const line = seedLine(s);
                  const shown = s.name ?? s.requested;
                  return (
                    <li key={i} data-testid={`seed-row-${i}`} data-outcome={s.outcome} className="flex flex-col">
                      <div className="flex items-center gap-2 flex-wrap">
                        {s.name ? (
                          <span
                            className="decklist-card cursor-help"
                            data-card-name={s.name}
                            onMouseEnter={(e) => showCardTooltip(cardImageUris(s.name!), e)}
                            onMouseLeave={hideCardTooltip}
                          >{shown}</span>
                        ) : <span>{shown}</span>}
                        <span
                          data-testid={`seed-outcome-${i}`}
                          className={SEED_PROBLEMS.has(s.outcome) ? 'text-on-surface-variant opacity-80' : 'text-primary'}
                        >{SEED_LABEL[s.outcome] ?? DAS_COPY.seedUnknown}</span>
                      </div>
                      {line && <span className="text-on-surface-variant opacity-70">{line}</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Role fill */}
          <div className="mb-3 p-3 bg-surface-container rounded-sm text-xs">
            <div className="font-semibold mb-1">{DAS_COPY.roleFillHeading}</div>
            <ul className="m-0 p-0 list-none flex flex-wrap gap-x-4 gap-y-1">
              {result.role_fill.map(r => (
                <li key={r.role} className="flex gap-1.5">
                  <span className="text-on-surface-variant">{roleLabel(r.role)}</span>
                  <span>{DAS_COPY.roleFillLine(r.filled, r.target)}</span>
                </li>
              ))}
            </ul>
            {result.shortfalls.length > 0 && (
              <div className="mt-1 text-warning-text">
                {DAS_COPY.shortfallsLine(result.shortfalls.map(roleLabel).join(', '))}
              </div>
            )}
          </div>

          {/* Fit against the goals the sketch was built for */}
          <div className="mb-3 p-3 bg-surface-container rounded-sm">
            <div className="text-xs font-semibold mb-1">{DAS_COPY.fitHeading}</div>
            <FitReadout fit={result.fit_report} goals={result.fit_report.goals} />
          </div>

          {/* Card list */}
          <DecklistWidget
            cards={cardEntries}
            cardMeta={cardMeta}
            tagGroups={tagGroups}
            commanders={[result.commander]}
            defaultGroupMode="type"
            canEdit={false}
          />

          {/* Build log */}
          {result.log.length > 0 && (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer text-on-surface-variant hover:text-on-surface select-none">
                {DAS_COPY.logSummary(result.log.length)}
              </summary>
              <div className="mt-2 flex flex-col gap-2">
                {result.log.map((entry, i) => (
                  <div key={i} className="p-2 bg-surface rounded-sm border border-outline-variant/40 leading-relaxed">
                    <AnalysisText text={entry} />
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Save section */}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={deckName}
              onChange={e => setDeckName(e.target.value)}
              placeholder={DAS_COPY.saveNamePlaceholder}
              className="flex-1 min-w-0 bg-surface border border-outline-variant rounded-sm text-on-surface text-sm py-1.5 px-3 outline-none focus:border-primary"
            />
            <button
              onClick={handleSave}
              disabled={isSaving || !deckName.trim() || saveProblem?.deckId != null}
              className="py-1.5 px-4 bg-primary text-on-primary border-none rounded-sm text-sm font-semibold cursor-pointer disabled:opacity-50 whitespace-nowrap"
            >
              {isSaving ? DAS_COPY.saving : DAS_COPY.saveDeck}
            </button>
            <button
              onClick={handleReject}
              disabled={isSaving}
              className="py-1.5 px-4 bg-transparent text-error-text border border-error-text/40 rounded-sm text-sm font-semibold cursor-pointer disabled:opacity-50 whitespace-nowrap hover:bg-error-text/10 transition-colors"
            >
              {DAS_COPY.reject}
            </button>
          </div>
          {saveProblem && (
            <div role="alert" className="mt-2 p-3 bg-status-warning-bg rounded-sm border border-warning-text/30 text-xs text-warning-text flex items-center gap-3 flex-wrap">
              <span>{saveProblem.message}</span>
              {saveProblem.deckId != null && (
                <button onClick={() => navigate(`/deck/${saveProblem.deckId}`)} className="underline">
                  {DAS_COPY.openDeck}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

